import type {
  EntitySignals,
  NetworkConnection,
  NetworkEvent,
  NetworkPattern,
  NetworkRole,
  NetworkSharedCompany,
  SignalMatch,
} from '../../../types/network'

const ROLE: Record<NetworkRole, string> = {
  owner: 'ejer',
  beneficial: 'reel ejer',
  management: 'direktør',
  board: 'bestyrelsesmedlem',
  founder: 'stifter',
  other: 'deltager',
}

const OWNERS: NetworkRole[] = ['owner', 'beneficial']

export function roleLabel(role: NetworkRole): string {
  return ROLE[role]
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * The one sentence that says why two people are connected, from the
 * company where they are closest: »Ejer Holm & Lind ApS sammen«.
 */
export function companyReason(c: NetworkSharedCompany): string {
  if (!c.together) return `I ${c.name}, men ikke samtidig`
  const now = c.current
  if (OWNERS.includes(c.role) && OWNERS.includes(c.subjectRole)) {
    return `${now ? 'Ejer' : 'Ejede'} ${c.name} sammen`
  }
  if (c.role === c.subjectRole) {
    if (c.role === 'board')
      return `${now ? 'Sidder' : 'Sad'} i bestyrelsen for ${c.name} sammen`
    if (c.role === 'management')
      return `${now ? 'Sidder' : 'Sad'} i direktionen for ${c.name} sammen`
    if (c.role === 'founder') return `Stiftede ${c.name} sammen`
  }
  return `${cap(roleLabel(c.role))} i ${c.name}, hvor personen ${now ? 'er' : 'var'} ${roleLabel(c.subjectRole)}`
}

/** »4 selskaber siden 2014«, »2011–2019«. */
export function connectionSpan(c: NetworkConnection): string {
  const parts: string[] = []
  if (c.sharedCount > 1) parts.push(`${c.sharedCount} selskaber`)
  if (c.fromYear !== null) {
    if (c.toYear === null) parts.push(`siden ${c.fromYear}`)
    else
      parts.push(
        c.toYear === c.fromYear
          ? String(c.fromYear)
          : `${c.fromYear}–${c.toYear}`
      )
  }
  return parts.join(' ')
}

export const EVENT_LABEL: Record<NetworkEvent, string> = {
  founded_together: 'Stiftede selskab sammen',
  joined_same_day: 'Trådte ind samme dag',
  left_together: 'Trådte ud samme dag',
  left_before_collapse: 'Trådte ud før konkurs',
}

export function periodLabel(
  from: string | null,
  to: string | null,
  current: boolean
): string {
  const y = (d: string | null) => (d ? d.slice(0, 4) : '?')
  if (current) return from ? `siden ${y(from)}` : 'nu'
  if (!from && !to) return ''
  return y(from) === y(to) ? y(from) : `${y(from)}–${y(to)}`
}

/**
 * ICIJ describes nodes in English (»Officer node from the Panama Papers
 * data.«); say it in Danish with the leak's name.
 */
export function offshoreText(description: string): string {
  const leak = description
    .match(/from the (.+?)(?: data)?(?: -.*)?\.?$/i)?.[1]
    ?.trim()
  const kind = /^officer/i.test(description)
    ? ' som person'
    : /^entity/i.test(description)
      ? ' som selskab'
      : /^intermediary/i.test(description)
        ? ' som mellemmand'
        : ''
  return leak
    ? `Navnet optræder${kind} i ${leak}`
    : 'Navnet optræder i ICIJ Offshore Leaks'
}

// ── Værd at vide ──────────────────────────────────────────────────────────────

export type FindingTone = 'warning' | 'danger' | 'info'

export interface Finding {
  key: string
  tone: FindingTone
  /** Sentence, may include links rendered by the component. */
  text: string
  /** Who says so, e.g. »Finanstilsynet«. */
  source: string
  /** How the name matched, for findings from outside CVR. */
  match?: SignalMatch
  href?: string
  /** Internal link targets to render inside the sentence. */
  links?: Array<{ label: string; to: string }>
}

const REGION: Record<string, string> = {
  dk: '',
  gl: ' (Grønland)',
  fo: ' (Færøerne)',
}
const SANCTION_LIST = {
  eu: "EU's sanktionsliste",
  ofac: "USA's sanktionsliste (OFAC)",
} as const

function monthsLabel(n: number): string {
  if (n === 0) return 'samme måned som'
  const abs = Math.abs(n)
  return `${abs} ${abs === 1 ? 'måned' : 'måneder'} ${n > 0 ? 'efter' : 'før'}`
}

/**
 * Findings for the strip at the top of the tab: register hits first
 * (sanctions, PEP, Folketinget, leaks), then patterns in the network.
 */
export function findings(
  signals: EntitySignals | null,
  patterns: NetworkPattern[]
): Finding[] {
  const out: Finding[] = []
  const hits = signals?.hits

  for (const s of hits?.sanctions ?? []) {
    out.push({
      key: `sanction-${s.source}-${s.ref}`,
      tone: 'danger',
      text: `Navnet står på ${SANCTION_LIST[s.source]}${s.programme ? ` · ${s.programme}` : ''}`,
      source: s.source === 'eu' ? 'EU' : 'US Treasury',
      match: s.match,
    })
  }
  for (const p of hits?.pep ?? []) {
    out.push({
      key: `pep-${p.name}-${p.region}-${p.current}`,
      tone: 'info',
      text: `${p.current ? 'På' : 'Tidligere på'} PEP-listen${REGION[p.region] ?? ''}: ${p.position || p.category}`,
      source: 'Finanstilsynet',
      match: p.match,
    })
  }
  for (const f of hits?.folketinget ?? []) {
    const extra = f.positions[0] ? ` · ${f.positions[0]}` : ''
    out.push({
      key: `ft-${f.name}-${f.party}`,
      tone: 'info',
      text: `${f.current ? 'Medlem' : 'Tidligere medlem'} af Folketinget${f.party ? ` for ${f.party}` : ''}${extra}`,
      source: 'Folketinget',
      match: f.match,
      href: f.url ?? undefined,
    })
  }
  for (const o of hits?.offshore ?? []) {
    out.push({
      key: `icij-${o.nodeId}`,
      tone: 'warning',
      text: offshoreText(o.description),
      source: 'ICIJ Offshore Leaks',
      match: o.match,
      href: o.url,
    })
  }

  for (const p of patterns) {
    if (p.kind === 'collapses') {
      const who = p.people[0]
      out.push({
        key: 'collapses',
        tone: 'warning',
        text:
          `${p.count} ${p.count === 1 ? 'selskab' : 'selskaber'}, personen har haft rolle i, gik konkurs eller blev tvangsopløst: ` +
          p.companies
            .slice(0, 3)
            .map(c => c.name)
            .join(', ') +
          (p.count > 3 ? ' m.fl.' : '') +
          (who ? `. ${who.navn} var med i ${who.count} af dem` : ''),
        source: 'CVR',
        links: [
          ...p.companies
            .slice(0, 3)
            .map(c => ({ label: c.name, to: `/company/${c.cvr}` })),
          ...(who
            ? [{ label: who.navn, to: `/person/${who.enhedsNummer}` }]
            : []),
        ],
      })
    } else if (p.kind === 'phoenix') {
      const where = p.same === 'trade' ? 'i samme branche' : 'på samme adresse'
      out.push({
        key: `phoenix-${p.old.cvr}-${p.new.cvr}`,
        tone: 'warning',
        text: `${p.new.name} blev stiftet ${monthsLabel(p.months)} ${p.old.name} gik konkurs, ${where}`,
        source: 'CVR',
        links: [
          { label: p.new.name, to: `/company/${p.new.cvr}` },
          { label: p.old.name, to: `/company/${p.old.cvr}` },
        ],
      })
    } else if (p.kind === 'directorships') {
      const parts = []
      if (p.active >= 10) parts.push(`${p.active} aktive direktørposter`)
      if (p.short >= 4)
        parts.push(`${p.short} direktørposter, der varede under et år`)
      out.push({
        key: 'directorships',
        tone: 'warning',
        text: parts.join(' og '),
        source: 'CVR',
      })
    } else if (p.kind === 'address_hub') {
      out.push({
        key: `hub-${p.address}`,
        tone: 'info',
        text: `${p.count} af personens selskaber har adresse på ${p.address}: ${p.companies
          .slice(0, 4)
          .map(c => c.name)
          .join(', ')}${p.count > 4 ? ' m.fl.' : ''}`,
        source: 'CVR',
        links: p.companies
          .slice(0, 4)
          .map(c => ({ label: c.name, to: `/company/${c.cvr}` })),
      })
    }
  }
  return out
}
